import { generateClient } from "aws-amplify/data";
import type { Schema } from "amplify/data/resource";
import type { JobStatus } from "./types";

const client = generateClient<Schema>();

/**
 * One row of a tool's run history.
 *
 * Distinct from {@link import("./types").JobRecord}, which models the single job
 * a tool sheet is watching. History rows carry the enqueue time and the chain
 * position instead of the live-progress fields the sheets subscribe to.
 */
export interface JobHistoryRow {
  id: string;
  type: string;
  status: JobStatus;
  triggeredBy: string;
  total: number | null;
  processed: number;
  counters: string | null;
  result: string | null;
  error: string | null;
  enqueuedAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  /** Job id of the first link of the sweep this row continues, if any. */
  chainRoot: string | null;
  /** 1-based position within that chain. */
  link: number | null;
}

/**
 * A single run, read on its own.
 *
 * Carries the raw `input` payload the list view has no column for — the
 * arguments a run was given are most of the value of inspecting it after it
 * has finished.
 */
export interface JobDetail extends JobHistoryRow {
  input: string | null;
}

export interface JobHistoryPage {
  rows: JobHistoryRow[];
  nextToken: string | null;
}

export interface JobHistoryRepository {
  listHistory(
    jobType: string,
    opts?: { limit?: number; nextToken?: string | null },
  ): Promise<JobHistoryPage>;
  latestJob(jobType: string): Promise<JobHistoryRow | null>;
  /** Resolves `null` for a row that no longer exists — jobs carry a `ttl`. */
  getJob(id: string): Promise<JobDetail | null>;
}

function asJsonString(value: unknown): string | null {
  if (typeof value === "string") return value;
  return value != null ? JSON.stringify(value) : null;
}

/** Chain position, which only chained runners record on their input payload. */
function readChain(input: unknown): {
  chainRoot: string | null;
  link: number | null;
} {
  const raw = asJsonString(input);
  if (!raw) return { chainRoot: null, link: null };
  try {
    const parsed = JSON.parse(raw) as { chainRoot?: unknown; link?: unknown };
    return {
      chainRoot: typeof parsed.chainRoot === "string" ? parsed.chainRoot : null,
      link: typeof parsed.link === "number" ? parsed.link : null,
    };
  } catch {
    return { chainRoot: null, link: null };
  }
}

function toHistoryRow(raw: Schema["Job"]["type"]): JobHistoryRow {
  return {
    id: raw.id,
    type: raw.type,
    status: (raw.status ?? "pending") as JobStatus,
    triggeredBy: raw.triggeredBy,
    total: raw.total ?? null,
    processed: raw.processed ?? 0,
    counters: asJsonString(raw.counters),
    result: asJsonString(raw.result),
    error: raw.error ?? null,
    enqueuedAt: raw.enqueuedAt ?? null,
    startedAt: raw.startedAt ?? null,
    completedAt: raw.completedAt ?? null,
    ...readChain(raw.input),
  };
}

function toJobDetail(raw: Schema["Job"]["type"]): JobDetail {
  return { ...toHistoryRow(raw), input: asJsonString(raw.input) };
}

const PAGE_SIZE = 25;

/**
 * Reads job history newest-first off the `byTypeEnqueuedAt` index.
 *
 * Rows enqueued before that index existed have no `enqueuedAt` and are absent
 * from it, so they never appear in history.
 */
export const jobHistoryRepository: JobHistoryRepository = {
  async listHistory(jobType, opts) {
    const result = await client.models.Job.byTypeEnqueuedAt(
      { type: jobType },
      {
        sortDirection: "DESC",
        limit: opts?.limit ?? PAGE_SIZE,
        nextToken: opts?.nextToken ?? undefined,
      },
    );
    if (result.errors?.length) throw new Error(result.errors[0].message);
    return {
      rows: result.data.map(toHistoryRow),
      nextToken: result.nextToken ?? null,
    };
  },

  async latestJob(jobType) {
    const { rows } = await this.listHistory(jobType, { limit: 1 });
    return rows[0] ?? null;
  },

  async getJob(id) {
    const result = await client.models.Job.get({ id });
    if (result.errors?.length) throw new Error(result.errors[0].message);
    return result.data ? toJobDetail(result.data) : null;
  },
};
